import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

// Confirms asking a project's owner for access.
//
// The button used to fire on the first click with no warning that it puts a
// request in a colleague's inbox. This is the pause before that, kept to the two
// facts that change what someone decides: who gets asked, and that the answer
// (including the permission level) is theirs, not yours.
//
// Styling follows the Projects-area modal convention (TransferOwnershipModal,
// ReassignProjectsModal): 2xl radius, right-aligned footer, #A04A97 primary.
const RequestAccessModal = ({
  isOpen,
  onClose,
  onConfirm,
  project,
  error = "",
  // True when this person asked before and the owner declined, so the copy can
  // acknowledge it rather than pretending this is a first ask.
  previouslyDenied = false,
}) => {
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // A note written for one project must not be sent about another, and a
  // reopened dialog must not still be showing the last one's success state.
  const projectId = project?.id;
  useEffect(() => {
    setNote("");
    setSending(false);
    setSent(false);
  }, [projectId, isOpen]);

  // The request is confirmed on the button itself before the dialog goes, so
  // the click has a visible result rather than the dialog just vanishing.
  // `onConfirm` throwing means it failed; the reason arrives via `error`.
  const handleConfirm = async () => {
    if (sending || sent) return;
    setSending(true);
    try {
      await onConfirm(note.trim());
      setSending(false);
      setSent(true);
      setTimeout(onClose, 900);
    } catch {
      setSending(false);
    }
  };

  const buttonLabel = () => {
    if (sent) return "Request sent";
    if (sending) return "Sending…";
    return previouslyDenied ? "Ask again" : "Send request";
  };

  if (!isOpen || !project) return null;

  const ownerName = project?.owner?.name || project?.owner?.email || "the owner";
  const strong = "font-medium text-[#3F3F46]";

  return createPortal(
    // React portals bubble events through the REACT tree, not the DOM tree, so
    // without this every click in here also reaches the table row this modal is
    // rendered from - which answers with the row's "you don't have access"
    // toast on every keystroke and button press. Stopping it at the root is the
    // fix; the portal's DOM position doesn't help.
    <div
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      className="fixed inset-0 z-[10000] bg-black/40 backdrop-blur-sm flex items-center justify-center font-outfit"
    >
      <div className="bg-white rounded-2xl shadow-lg w-[460px] px-6 py-5">
        <h2 className="text-[#18181B] text-[20px] font-semibold mb-1">
          {previouslyDenied ? "Ask again for access?" : "Request access?"}
        </h2>

        <p className="text-[#71717A] text-[14px] leading-relaxed">
          {previouslyDenied ? (
            <>
              <span className={strong}>{ownerName}</span> declined your last request for{" "}
              <span className={strong}>{project.name}</span>. We'll ask again.
            </>
          ) : (
            <>
              We'll ask <span className={strong}>{ownerName}</span> for access to{" "}
              <span className={strong}>{project.name}</span>. They choose whether you get editor
              or reviewer access.
            </>
          )}
        </p>

        <label
          htmlFor="request-access-note"
          className="block text-[13px] text-[#3F3F46] font-medium mt-4 mb-1"
        >
          Add a note <span className="text-[#A1A1AA] font-normal">(optional)</span>
        </label>
        {/* The placeholder: utilities below are not decoration. Four bare
            `textarea::placeholder` rules are loaded app-wide from other pages'
            stylesheets; the winning one sets 16px/600, and another leaks
            position:absolute + width:170px onto every placeholder in the app.
            Class specificity beats those element rules, but only for the
            properties named explicitly. */}
        <textarea
          id="request-access-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={sending}
          rows={2}
          maxLength={500}
          placeholder="Why you need it"
          className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-[14px] font-normal outline-none focus:border-[#C95EBE] resize-none disabled:opacity-60 placeholder:text-[13px] placeholder:font-normal placeholder:text-[#A1A1AA] placeholder:static placeholder:w-auto"
        />

        {error ? <p className="text-[13px] text-[#E21919] mt-3">{error}</p> : null}

        <div className="flex justify-end gap-3 mt-5">
          <button
            onClick={onClose}
            disabled={sending || sent}
            className="px-5 py-2 rounded-md border border-[#E4E4E7] text-[#52525B] text-[14px] font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={sending || sent}
            className={`px-5 py-2 rounded-md text-[14px] font-semibold transition-colors min-w-[140px] disabled:cursor-not-allowed ${
              sent
                ? "bg-[#F0FAF4] border border-green-500 !text-green-600"
                : "bg-[#A04A97] text-white hover:bg-[#8a3f83] disabled:opacity-60"
            }`}
          >
            {buttonLabel()}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RequestAccessModal;
